<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useQuery } from '@tanstack/vue-query'
import axios from 'axios'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import AppFooter from '@/components/AppFooter.vue'
import IconRenderer from '@/components/IconRenderer.vue'

const router = useRouter()
const { locale, t } = useI18n({ useScope: 'global' })

const langPopover = ref()

const ALL_UI_LANGS = [
  { code: 'bg', label: 'Bulgarian' },
  { code: 'cs', label: 'Czech' },
  { code: 'da', label: 'Danish' },
  { code: 'de', label: 'German' },
  { code: 'el', label: 'Greek' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'et', label: 'Estonian' },
  { code: 'fi', label: 'Finnish' },
  { code: 'fr', label: 'French' },
  { code: 'ga', label: 'Irish' },
  { code: 'hr', label: 'Croatian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'it', label: 'Italian' },
  { code: 'lt', label: 'Lithuanian' },
  { code: 'lv', label: 'Latvian' },
  { code: 'mt', label: 'Maltese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'pl', label: 'Polish' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ro', label: 'Romanian' },
  { code: 'sk', label: 'Slovak' },
  { code: 'sl', label: 'Slovenian' },
  { code: 'sv', label: 'Swedish' },
]

function setLocale(code: string) {
  locale.value = code
  localStorage.setItem('ui_locale', code)
  langPopover.value?.hide()
}

const { data: landingSessions } = useQuery({
  queryKey: ['landing-sessions'],
  queryFn: () =>
    axios.get('/api/v1/supervisor/landing/sessions/').then((r) => r.data as LandingSession[]),
  staleTime: 5 * 60 * 1000,
  retry: false,
})

interface LandingSession {
  course_title: string
  icon_name: string
  icon_color: string
  icon_background: string
  date: string
  status: string
}

const features = computed(() => [
  { icon: '📝', title: t('landing.features.quiz.title'), description: t('landing.features.quiz.description') },
  { icon: '🏅', title: t('landing.features.certificates.title'), description: t('landing.features.certificates.description') },
  { icon: '🔒', title: t('landing.features.compliance.title'), description: t('landing.features.compliance.description') },
])
</script>

<template>
  <div class="min-h-screen flex flex-col">
    <!-- Hero -->
    <div class="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-indigo-800 to-indigo-600 flex-1">
      <!-- Background glow -->
      <div class="absolute -top-40 -right-24 w-96 h-96 rounded-full bg-violet-500/20 blur-3xl pointer-events-none" />
      <div class="absolute -bottom-20 -left-12 w-64 h-64 rounded-full bg-indigo-400/15 blur-3xl pointer-events-none" />

      <!-- Navbar -->
      <nav class="relative z-10 flex items-center justify-between px-6 py-4 md:px-10">
        <!-- Logo -->
        <div class="flex items-center gap-2.5">
          <img src="/logo.png" alt="supervisor-ng" class="h-8 w-8 object-contain" />
          <span class="text-white font-extrabold text-base tracking-tight">Supervisor</span>
        </div>

        <!-- Nav actions -->
        <div class="flex items-center gap-1">
          <!-- Language switcher -->
          <Button
            text
            rounded
            aria-label="Switch language"
            class="!text-white/80 hover:!text-white hover:!bg-white/10"
            @click="langPopover.toggle($event)"
          >
            <span class="text-xs font-semibold tracking-wide uppercase">{{ locale }}</span>
          </Button>
          <Popover ref="langPopover">
            <div class="py-1 w-44 max-h-72 overflow-y-auto">
              <button
                v-for="lang in ALL_UI_LANGS"
                :key="lang.code"
                class="w-full flex items-center justify-between px-3 py-1.5 text-sm hover:bg-surface-100 dark:hover:bg-surface-700 rounded transition-colors"
                :class="locale === lang.code ? 'font-semibold text-primary-600' : 'text-surface-700'"
                @click="setLocale(lang.code)"
              >
                <span>{{ lang.label }}</span>
                <span class="text-xs text-surface-400 uppercase">{{ lang.code }}</span>
              </button>
            </div>
          </Popover>

          <!-- Sign in -->
          <Button
            :label="t('landing.nav.signin')"
            size="small"
            class="!bg-white/15 !border-white/30 !text-white hover:!bg-white/25"
            outlined
            @click="router.push({ name: 'login' })"
          />
        </div>
      </nav>

      <!-- Desktop: split layout / Mobile: stacked -->
      <div class="relative z-10 max-w-6xl mx-auto px-6 py-12 md:py-20 md:flex md:items-center md:gap-16">
        <!-- Text -->
        <div class="flex-1 text-center md:text-left mb-10 md:mb-0">
          <h1 class="text-3xl md:text-5xl font-black text-white leading-tight tracking-tight mb-4">
            {{ t('landing.hero.tagline') }}<br /><span class="text-indigo-300">{{ t('landing.hero.taglineHighlight') }}</span>
          </h1>
          <p class="text-sm md:text-base text-white/70 leading-relaxed mb-8 max-w-md mx-auto md:mx-0">
            {{ t('landing.hero.subtitle') }}
          </p>
          <!-- CTAs — quiz first (primary), login second (secondary) -->
          <div class="flex flex-col gap-3 md:flex-row">
            <Button
              :label="t('landing.hero.ctaQuiz')"
              class="!bg-white !text-indigo-700 !border-transparent font-bold hover:!bg-white/90"
              @click="router.push({ name: 'quiz-access-enter' })"
            />
            <Button
              :label="t('landing.hero.ctaSignin')"
              class="!bg-transparent !border-white/40 !text-white hover:!bg-white/10"
              outlined
              @click="router.push({ name: 'login' })"
            />
          </div>
        </div>

        <!-- Recent sessions card (desktop only) -->
        <div class="hidden md:block w-96 shrink-0">
          <div class="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-sm p-5">
            <p class="text-white font-bold text-sm mb-3">📋 {{ t('landing.card.title') }}</p>
            <template v-if="landingSessions && landingSessions.length > 0">
              <div
                v-for="item in landingSessions"
                :key="item.course_title + item.date"
                class="flex items-center gap-3 py-2.5 border-b border-white/10 last:border-0"
              >
                <div
                  class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  :style="item.icon_background ? `background-color: ${item.icon_background}` : 'background: rgba(255,255,255,0.15)'"
                >
                  <IconRenderer
                    v-if="item.icon_name"
                    :icon="item.icon_name"
                    :size="16"
                    :style="item.icon_color ? `color: ${item.icon_color}` : 'color: white'"
                  />
                  <i v-else class="pi pi-graduation-cap text-white/70 text-xs" />
                </div>
                <span class="text-white/85 text-xs font-semibold flex-1 line-clamp-2">{{ item.course_title }}</span>
                <span class="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-500/20 text-slate-300 shrink-0">
                  {{ new Date(item.date).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }) }}
                </span>
              </div>
            </template>
            <template v-else>
              <div class="flex items-center gap-3 py-2 opacity-50">
                <i class="pi pi-calendar text-white/60 text-sm" />
                <span class="text-white/60 text-xs">{{ t('landing.card.noSessions') }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Features strip -->
    <div class="bg-white border-b border-slate-100">
      <div class="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div v-for="feat in features" :key="feat.title" class="flex items-start gap-3">
          <span class="text-2xl">{{ feat.icon }}</span>
          <div>
            <p class="font-bold text-sm text-slate-800">{{ feat.title }}</p>
            <p class="text-xs text-slate-500 mt-0.5 leading-relaxed">{{ feat.description }}</p>
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>
